/**
 * Statuses of `GET /company/payment-setup` (the backend's CompanyPaymentSetupStatus).
 */
export const PaymentSetupStatus = {
  WaitingForPartner: 'waiting_for_partner',
  OnlinePaymentsOff: 'online_payments_off',
  NotSetUp: 'not_set_up',
  BusinessDetailsMissing: 'business_details_missing',
  Provisioning: 'provisioning',
  HostedNotStarted: 'hosted_not_started',
  HostedIncomplete: 'hosted_incomplete',
  LinkExpired: 'link_expired',
  Submitted: 'submitted',
  ActionNeeded: 'action_needed',
  Verified: 'verified',
};

/**
 * The partner-side badges (`group` on the payment-setup resources) the Restaurants
 * table's "Needs attention" filter counts — the backend's
 * SummariseCompanyPaymentSetupStatusAction, mirrored for the per-connection line.
 */
export const NEEDS_ATTENTION_GROUPS = ['not_set_up', 'business_details_missing', 'hosted_not_finished'];

/** The phases a link into the wizard may open — the owner's reminder email names one. */
export const LINKABLE_PHASES = ['payouts', 'bank_account'];

/**
 * Steps an older link may still name. Business details moved onto the Online payment
 * step, so a reminder email sent before that opens it there.
 */
const RETIRED_PHASE_LINKS = { business_details: 'payouts' };

/** The restaurant a link into the wizard was made for, when it names one. */
export function linkedCompanyId(search) {
  return new URLSearchParams(search).get('company');
}

/**
 * Which step a link into the wizard asks for — the provider's return URL and the
 * owner's reminder email both name one. A link naming another restaurant does not
 * steer this one.
 */
export function requestedWizardPhase(search, companyId, allowedPhases) {
  const params = new URLSearchParams(search);
  const phase = RETIRED_PHASE_LINKS[params.get('phase')] ?? params.get('phase');

  if (params.has('company') && params.get('company') !== String(companyId)) {
    return null;
  }

  return allowedPhases.includes(phase) ? phase : null;
}

/** The provider's form has been opened at least once, so there is something to report back. */
/** A connection the backend still serialises after it was disconnected is not a link. */
export function isGatewayLinked(setup) {
  return setup?.connection?.status === 'connected';
}

export const RETURN_STATES = [
  PaymentSetupStatus.HostedIncomplete,
  PaymentSetupStatus.LinkExpired,
  PaymentSetupStatus.Submitted,
  PaymentSetupStatus.ActionNeeded,
];

/**
 * The external-completion map the wizard shell feeds `useOnboardingWizard`. The
 * Online payment step is done once payments are live — verified on Adyen or
 * Stripe, or linked and set up on the partner's own gateway — or once the
 * restaurant chose "Not now" and takes cash only. Anything in between (waiting
 * for the provider, form not finished) is not done; Go Live decides what that
 * blocks.
 */
export function computePayoutsExternalCompletion(setup) {
  const status = setup?.status;

  return { payouts: status === PaymentSetupStatus.Verified || status === PaymentSetupStatus.OnlinePaymentsOff };
}

/**
 * Whether a restaurant still owes the Online payment step, so a link may reopen
 * it after go-live. "Not now" completes the step for Go Live, but a restaurant
 * that went live cash only can come back to turn online payments on — the step
 * is the only place that answer can be changed.
 */
export function isPayoutsStepOwed(setup) {
  if (!setup) {
    return false;
  }

  return setup.status === PaymentSetupStatus.OnlinePaymentsOff || !computePayoutsExternalCompletion(setup).payouts;
}

/** The statuses in which Payment methods offers a way into the Online payment step. */
export const CONFIGURE_ONLINE_PAYMENTS_STATES = [PaymentSetupStatus.OnlinePaymentsOff, PaymentSetupStatus.NotSetUp];

/** The wizard's Online payment step for one restaurant, as a link can open it after go-live. */
export function onlinePaymentStepPath(onboardingPath, companyId) {
  return `${onboardingPath}?phase=payouts&company=${encodeURIComponent(companyId)}`;
}

/**
 * The provider has given its final answer: nothing more will change without the
 * owner or the provider acting, so the return poll can stop early. "Submitted" is
 * not one of them — in test mode especially, "verified" often follows seconds later.
 */
export const FINAL_RETURN_STATES = [PaymentSetupStatus.Verified, PaymentSetupStatus.ActionNeeded];

/**
 * Where the provider sends the owner back to: this step, with a marker to poll on
 * and the restaurant it was for — a partner who switched restaurant in another
 * tab while on the provider's page must not poll, or be told about, the wrong one.
 */
export function buildPaymentReturnUrl(origin, onboardingPath, companyId) {
  return `${origin}${onboardingPath}?phase=payouts&payment_return=1&company=${encodeURIComponent(companyId)}`;
}

/** Whether the current URL is a return from the provider for this restaurant. */
export function isPaymentReturnFor(search, companyId) {
  const params = new URLSearchParams(search);

  return params.has('payment_return') && params.get('company') === String(companyId);
}

/** The same marker on any page — the KYC banner hands off from wherever the owner is. */
export function withPaymentReturn(href, companyId) {
  const url = new URL(href);
  url.searchParams.set('payment_return', '1');
  url.searchParams.set('company', String(companyId));

  return url.toString();
}

/** Takes the marker (and the step it opened) out of the URL once the poll is done. */
export function clearPaymentReturn() {
  const url = new URL(window.location.href);
  ['payment_return', 'company', 'phase'].forEach((param) => url.searchParams.delete(param));
  // Only the query goes; the route is the one already rendered, so the router has
  // nothing to re-match.
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * When each status read runs after returning from the provider, in ms from the
 * return: six reads across ten seconds. The webhooks behind the status usually
 * land within that window; whatever has not by then is shown as it stands.
 */
export const RETURN_POLL_SCHEDULE_MS = [0, 2000, 4000, 6000, 8000, 10000];

/** Fired once the return poll is done, so every reader of the status refreshes. */
export const PAYMENT_RETURN_SETTLED_EVENT = 'payment-setup:return-settled';
